import { LINK_MOVE_TYPE } from 'core/constants'
import { BRIDGE } from 'core/constants/bridge'
import { getUA } from 'core/singleton'
import { STORE_AIRBRIDGE } from 'core/store/analysis'
import { IS_OPEN_LINK, STORE_ROOT_PROPS } from 'core/store/global'
import { formatYYMMDDDDD } from 'core/util/date'
import { requestBridge } from 'core/util/default'
import { moveToLink } from 'core/util/url'
import React, { useCallback, useMemo } from 'react'
import { useRecoilValue } from 'recoil'
import styled, { css } from 'styled-components'

export interface CardInfoProps {
  broadcastId?: string
  customButtonInfo?: CollectConfig
  programmingStartDt?: string
  shopLogo?: string
  shopName?: string
  small?: boolean
  title?: string
}

const CardInfo: React.FC<CardInfoProps> = ({
  broadcastId,
  customButtonInfo,
  programmingStartDt,
  shopLogo,
  shopName,
  small,
  title,
}) => {
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const root = useRecoilValue(STORE_ROOT_PROPS)

  const { isSDK } = getUA()

  const src = shopLogo + '?h=56&w=56&f=jpg'

  const handleCustomButtonClick = useCallback(
    (link?: string) => () => {
      if (!broadcastId || !link) return

      if (root?.bridge || window[BRIDGE.BROADCAST_CUSTOM_BUTTON] || isSDK) {
        requestBridge(BRIDGE.BROADCAST_CUSTOM_BUTTON, {
          broadcastId,
          customButtonUrl: link,
        })
      } else {
        moveToLink({
          airbridge,
          link,
          type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
        })
      }
    },
    [airbridge, broadcastId, isOpenLink, isSDK, root],
  )

  const customButtonEl = useMemo(() => {
    if (!customButtonInfo) return null

    const { backgroundColor, borderColor, collectName, collectUrl, fontColor } = customButtonInfo

    return (
      <StyledCustomButton
        backgroundColor={backgroundColor}
        borderColor={borderColor}
        fontColor={fontColor}
        type={'button'}
        onClick={handleCustomButtonClick(collectUrl)}
      >
        {collectName}
      </StyledCustomButton>
    )
  }, [customButtonInfo, handleCustomButtonClick])

  return (
    <StyledInfo small={small}>
      {programmingStartDt ? (
        <div className="date">
          {formatYYMMDDDDD({
            date: new Date(programmingStartDt),
            delimiter: '.',
          })}
        </div>
      ) : null}
      <div className="info-title">{title}</div>
      <div className="partner_info">
        <div
          className="partner_logo"
          tabIndex={src ? 0 : -1}
          style={{ backgroundImage: `url(${src})` }}
        />
        <span className="partner_name">{shopName}</span>
      </div>
      {customButtonEl ? customButtonEl : null}
    </StyledInfo>
  )
}

export default CardInfo

const StyledInfo = styled.div<{ small?: boolean }>`
  .date {
    color: #b3b3b3;
    font-size: 14px;
  }

  .info-title {
    margin-top: 2px;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 14px;
    line-height: 20px;
    white-space: nowrap;
    font-weight: 600;
    color: ${(props) => props.theme.font.primary};
  }

  .partner_info {
    display: flex;
    align-items: center;
    margin-top: 6px;

    .partner_logo {
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: solid 1px #edeff0;
      background-repeat: no-repeat;
      background-size: cover;
      background-position: center;
      margin-right: 8px;
      ${(props) =>
        props.small &&
        css`
          display: none;
        `}
    }

    .partner_name {
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
      color: ${(props) => props.theme.shopNameFont};
    }
    ${(props) =>
      !props.theme.shopName &&
      css`
        display: none;
      `}
  }

  .custom-button {
    padding: 4px 6px;
    margin-top: 16px;

    border-color: #00bc70;
    background-color: #ffffff;
    color: #00bc70;
  }
`

const StyledCustomButton = styled.button<{
  backgroundColor: string
  borderColor: string
  fontColor: string
}>`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 4px 6px;
  border: 1px solid;
  border-radius: 4px;
  margin-top: 16px;
  height: 22px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${(props) => props.backgroundColor};
  border-color: ${(props) => props.borderColor};
  color: ${(props) => props.fontColor} !important;
  cursor: pointer;
`
